// Request-body / query schemas shared by the API (enforcement) and web (UX).
import { z } from "zod";
import {
  CONTRACTOR_INVOICE_STATUSES,
  CURRENCIES,
  ENTRY_TYPES,
  EXPENSE_CATEGORIES,
  EXPENSE_INVOICE_STATUSES,
  EXPENSE_SORT_FIELDS,
  EXPENSE_VALIDATION_STATUSES,
  INVOICE_SORT_FIELDS,
  MAX_INTAKE_PAGE,
  PAYMENT_SORT_FIELDS,
  INVOICE_VALIDATION_STATUSES,
  PROJECT_STATUSES,
  STAFF_ROLES,
  QUEUE_SORT_FIELDS,
  RATE_UNITS,
  REPORT_GROUP_BY,
  REPORT_SCOPES,
  SORT_DIRECTIONS,
  TIMESHEET_STATUSES,
  USER_STATUSES,
} from "./constants.js";
import { basQuartersBetween, fromYmd, isValidYearMonth, isValidYmd, parseBasQuarter } from "./dates.js";
import { CORRECTABLE_FIELDS } from "./invoice-normalise.js";
import {
  HOURS_STEP,
  MAX_BULK_APPROVE_WEEKS,
  MAX_CHASE_WEEKS,
  MAX_COMMENT_LENGTH,
  MAX_DAILY_RATE,
  MAX_HOURLY_RATE,
  MAX_HOURS_PER_DAY,
  MAX_INVOICE_LINES,
  MAX_NOTE_LENGTH,
  MAX_PAYMENT_REFERENCE_LENGTH,
  RATE_STEP,
} from "./rules.js";

export const ymdSchema = z
  .string()
  .refine(isValidYmd, { message: "Expected a valid YYYY-MM-DD date" });

/**
 * An untouched optional form field arrives as "", which means "not provided"
 * rather than "the empty string" — normalise it so the API stores NULL.
 *
 * `.optional()` comes *last*. A `.transform()` applied after it yields a
 * **required** key typed `string | undefined`, which forces every caller to spell
 * the field out — fine for a create form that always sends all of them, useless
 * for a PATCH that sends only what changed.
 */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v ? v : undefined))
    .optional();

const optionalEmail = z
  .union([z.literal(""), z.email().max(254)])
  .transform((v) => (v ? v.toLowerCase() : undefined))
  .optional();

/**
 * The PATCH counterparts of the two above.
 *
 * On a create, "" and "absent" mean the same thing, so collapsing both to
 * `undefined` is right. On an **update** they are opposites: clearing a field is
 * something the admin did, and the service can only tell "leave it alone" from
 * "store NULL" by whether the key is there. Collapsing "" to `undefined` made a
 * cleared box unsendable — `JSON.stringify` drops the key, the service sees
 * nothing, and the old value came back on the next render.
 *
 * So: `null` (or "") clears; omitting the key still means leave it alone. This is
 * the same contract the project dates and budget already use.
 */
const clearableText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional();

const clearableEmail = z
  .union([z.literal(""), z.email().max(254)])
  .transform((v) => (v ? v.toLowerCase() : null))
  .nullable()
  .optional();

export const loginSchema = z.object({
  email: z.email().max(254),
  password: z.string().min(1).max(128),
});
export type LoginInput = z.infer<typeof loginSchema>;

/** Any password a person chooses. Server-generated temp passwords bypass this. */
export const passwordSchema = z.string().min(12).max(128);

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1).max(128),
    newPassword: passwordSchema,
  })
  .refine((v) => v.newPassword !== v.currentPassword, {
    message: "The new password must differ from the current one.",
    path: ["newPassword"],
  });
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const entrySchema = z.object({
  date: ymdSchema,
  type: z.enum(ENTRY_TYPES),
  hours: z.number().min(0).max(MAX_HOURS_PER_DAY).multipleOf(HOURS_STEP),
  comment: z.string().max(MAX_NOTE_LENGTH),
});

export const saveTimesheetSchema = z.object({
  projectId: z.string().min(1).max(64),
  weekStart: ymdSchema,
  /**
   * Which half of a split week this is (TIME-1); absent is the week's first
   * period, which for eleven weeks in twelve is the whole week.
   */
  periodStart: ymdSchema.optional(),
  // One per day of the period, 1–7 of them. The exact count is checked by
  // `validateWeekEntries`, which knows where the month falls; zod does not.
  entries: z.array(entrySchema).min(1).max(7),
  submit: z.boolean(),
  confirmOvertime: z.boolean().optional().default(false),
  /**
   * Why there are no worked hours (TIME-15). Kept only while the sheet has none;
   * required to submit one that has none.
   */
  nilNote: z.string().trim().max(MAX_NOTE_LENGTH).nullable().optional(),
  /** The version the client read (TIME-11). 0 means "this week did not exist yet". */
  version: z.number().int().nonnegative(),
});
export type SaveTimesheetInput = z.infer<typeof saveTimesheetSchema>;

/** A contractor recalls their own pending sheet so it can be edited again. */
export const recallTimesheetSchema = z.object({
  projectId: z.string().min(1).max(64),
  weekStart: ymdSchema,
  /** Which half of a split week (TIME-1); absent is the week's first period. */
  periodStart: ymdSchema.optional(),
  version: z.number().int().nonnegative(),
});
export type RecallTimesheetInput = z.infer<typeof recallTimesheetSchema>;

export const rejectTimesheetSchema = z.object({
  note: z.string().trim().min(3).max(MAX_COMMENT_LENGTH),
});
export type RejectTimesheetInput = z.infer<typeof rejectTimesheetSchema>;

export const reviewTimesheetWeekSchema = z.object({
  ids: z.array(z.string().min(1).max(64)).min(1).max(100),
});
export const rejectTimesheetWeekSchema = reviewTimesheetWeekSchema.extend({
  note: z.string().trim().min(3).max(MAX_COMMENT_LENGTH),
});

export const myTimesheetQuerySchema = z.object({
  projectId: z.string().min(1).max(64).optional(),
  week: ymdSchema.optional(),
  /** First day of the half of a split week (TIME-1). Absent is the week's first period. */
  period: ymdSchema.optional(),
});
export type MyTimesheetQuery = z.infer<typeof myTimesheetQuerySchema>;

export const myTimesheetsQuerySchema = z.object({
  projectId: z.string().min(1).max(64).optional(),
  from: ymdSchema.optional(),
  to: ymdSchema.optional(),
  /**
   * Ask for each week's seven daily hours as well as its total.
   *
   * Opt-in, and load-bearing that it is. This is the one contractor-facing query
   * DATA-7 names as unpaginated, and the submissions page fetches it with no date
   * range at all — every week the contractor has ever filed. Loading entries
   * unconditionally would multiply that by seven. The calendar always passes a
   * month range and sets this; submissions never does.
   *
   * `z.enum(["1"])` rather than `z.coerce.boolean()`, which reads the string
   * "false" as true — a query string has no booleans, only text.
   */
  days: z.enum(["1"]).optional(),
});
export type MyTimesheetsQuery = z.infer<typeof myTimesheetsQuerySchema>;

/**
 * Pagination, with a ceiling.
 *
 * `pageSize` is capped rather than merely defaulted: an unbounded list is
 * unbounded whether the caller asked for it or not (DATA-7), and `?pageSize=99999`
 * would put us straight back where we started.
 */
export const MAX_PAGE_SIZE = 100;
export const DEFAULT_PAGE_SIZE = 25;

const pageSchema = z.coerce.number().int().min(1).optional().default(1);
const pageSizeSchema = z.coerce
  .number()
  .int()
  .min(1)
  .max(MAX_PAGE_SIZE)
  .optional()
  .default(DEFAULT_PAGE_SIZE);

/**
 * The filter vocabulary shared by the queue, the chase list, reports and every
 * export of them.
 *
 * Spread as a **shape literal**, never composed via `.extend()` on a base
 * schema: `.refine()` returns a `ZodEffects`, which has no `.extend`/`.omit`, so
 * the first schema to add a cross-field rule would break every schema derived
 * from it. Spreading keeps each schema a plain object that can refine freely.
 */
const timesheetFilterShape = {
  /** Free-text over contractor, client and project names. */
  q: z.string().trim().max(120).optional(),
  contractorId: z.string().min(1).max(64).optional(),
  projectId: z.string().min(1).max(64).optional(),
  clientId: z.string().min(1).max(64).optional(),
  from: ymdSchema.optional(),
  to: ymdSchema.optional(),
};

/** `to` before `from` is a typo, not an empty result — say so. */
const rangeOk = (v: { from?: string; to?: string }) => !v.from || !v.to || v.to >= v.from;
const rangeOkMessage = {
  message: "The end date must be on or after the start date.",
  path: ["to"],
};

export const timesheetFiltersSchema = z
  .object(timesheetFilterShape)
  .refine(rangeOk, rangeOkMessage);
export type TimesheetFilters = z.infer<typeof timesheetFiltersSchema>;

export const adminTimesheetsQuerySchema = z
  .object({
    ...timesheetFilterShape,
    status: z
      .enum([...TIMESHEET_STATUSES, "ALL"])
      .optional()
      .default("SUBMITTED"),
    /** Pin to one week. Narrower than `from`/`to` and kept because the queue links use it. */
    weekStart: ymdSchema.optional(),
    sort: z.enum(QUEUE_SORT_FIELDS).optional().default("submittedAt"),
    dir: z.enum(SORT_DIRECTIONS).optional().default("desc"),
    page: pageSchema,
    pageSize: pageSizeSchema,
  })
  .refine(rangeOk, rangeOkMessage);
export type AdminTimesheetsQuery = z.infer<typeof adminTimesheetsQuerySchema>;
/**
 * The same query as it travels on the wire, before zod applies defaults.
 *
 * The API contract is typed with this rather than the parsed shape: a client
 * that omits `sort` is not sending `sort: "submittedAt"`, it is sending nothing,
 * and typing the request as though the defaults were present would force every
 * link to restate them — and put them in the cache key.
 */
export type AdminTimesheetsQueryInput = z.input<typeof adminTimesheetsQuerySchema>;

export const timesheetsExportQuerySchema = z
  .object({
    ...timesheetFilterShape,
    status: z
      .enum([...TIMESHEET_STATUSES, "ALL"])
      .optional()
      .default("ALL"),
  })
  .refine(rangeOk, rangeOkMessage);
export type TimesheetsExportQuery = z.infer<typeof timesheetsExportQuerySchema>;
export type TimesheetsExportQueryInput = z.input<typeof timesheetsExportQuerySchema>;

export const yearMonthSchema = z
  .string()
  .refine(isValidYearMonth, { message: "Expected a month as YYYY-MM." });

/**
 * The client-safe month report: one contractor, one client, one month.
 *
 * All three are **required**, which is why this is written out rather than spread
 * from `timesheetFilterShape`. That shape makes every field optional and admits
 * `q` and `projectId` — on a file whose whole purpose is to be forwarded to one
 * client, an optional `clientId` is a scope that can silently widen, and a `q`
 * is a second way to decide what lands in it.
 */
export const contractorMonthReportQuerySchema = z.object({
  contractorId: z.string().min(1).max(64),
  clientId: z.string().min(1).max(64),
  month: yearMonthSchema,
});
export type ContractorMonthReportQuery = z.infer<typeof contractorMonthReportQuerySchema>;
export type ContractorMonthReportQueryInput = z.input<
  typeof contractorMonthReportQuerySchema
>;

/**
 * Bulk approve/unapprove.
 *
 * The unit is a **contractor-week group**, not a sheet: a week can span several
 * projects and approving half of one is not a state the queue can render or a
 * contractor can act on. Grouping in the request shape is what makes a partial
 * failure land on week boundaries.
 */
export const bulkReviewSchema = z.object({
  weeks: z
    .array(z.object({ ids: z.array(z.string().min(1).max(64)).min(1).max(20) }))
    .min(1)
    .max(MAX_BULK_APPROVE_WEEKS),
});
export type BulkReviewInput = z.infer<typeof bulkReviewSchema>;

/** A chase range longer than this can't finish inside the gateway window. */
const rangeWithinChaseCap = (v: { from?: string; to?: string }) => {
  if (!v.from || !v.to) return true;
  const weeks =
    (fromYmd(v.to).getTime() - fromYmd(v.from).getTime()) / (7 * 24 * 60 * 60 * 1000);
  return weeks <= MAX_CHASE_WEEKS;
};

export const chaseQuerySchema = z
  .object(timesheetFilterShape)
  .refine(rangeOk, rangeOkMessage)
  .refine(rangeWithinChaseCap, {
    message: `Pick a range of ${MAX_CHASE_WEEKS} weeks or fewer.`,
    path: ["to"],
  });
export type ChaseQuery = z.infer<typeof chaseQuerySchema>;
export type ChaseQueryInput = z.input<typeof chaseQuerySchema>;

export const reportsQuerySchema = z
  .object({
    ...timesheetFilterShape,
    groupBy: z.enum(REPORT_GROUP_BY).optional().default("client"),
    scope: z.enum(REPORT_SCOPES).optional().default("APPROVED_AND_SUBMITTED"),
    page: pageSchema,
    pageSize: pageSizeSchema,
  })
  .refine(rangeOk, rangeOkMessage);
export type ReportsQuery = z.infer<typeof reportsQuerySchema>;
export type ReportsQueryInput = z.input<typeof reportsQuerySchema>;

/**
 * The admin dashboard's one filter: a date range. Deliberately nothing else.
 * Every panel below the filter answers for the same slice, and the invoice and
 * expense figures have no client to narrow by, so a client filter would leave
 * half the page quietly ignoring it.
 */
export const dashboardQuerySchema = z
  .object({ from: ymdSchema.optional(), to: ymdSchema.optional() })
  .refine(rangeOk, rangeOkMessage);
export type DashboardQuery = z.infer<typeof dashboardQuerySchema>;

// --- Admin entity management (Phase 2) ------------------------------------
// The old app took a plain-text temp password from the form; here the server
// generates it and returns it exactly once, so it is never typed, logged, or
// chosen weakly.

export const createContractorSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    email: z.email().max(254),
    companyName: optionalText(200),
    projectId: z.string().min(1).max(64).optional(),
    dailyRate: z
      .number()
      .nonnegative()
      .max(MAX_DAILY_RATE)
      .multipleOf(RATE_STEP)
      .optional(),
  })
  .refine((v) => v.dailyRate === undefined || v.projectId !== undefined, {
    message: "Choose a project before setting a daily rate.",
    path: ["dailyRate"],
  });
export type CreateContractorInput = z.infer<typeof createContractorSchema>;

export const updateContractorStatusSchema = z.object({
  status: z.enum(USER_STATUSES),
});
export type UpdateContractorStatusInput = z.infer<typeof updateContractorStatusSchema>;

/**
 * Correcting a record, rather than replacing it. Every field is optional, and a
 * request that changes nothing is refused — an empty PATCH is a bug in the caller,
 * not a no-op worth honouring.
 */
const atLeastOneField = { message: "Nothing to update." };
const notEmpty = (v: object) => Object.values(v).some((x) => x !== undefined);

export const updateContractorSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    email: z.email().max(254).optional(),
    /** `null` clears the company; omitting the key leaves it alone. */
    companyName: clearableText(200),
  })
  .refine(notEmpty, atLeastOneField);
export type UpdateContractorInput = z.infer<typeof updateContractorSchema>;

// --- admins ----------------------------------------------------------------
// Inviting an admin is inviting a contractor with the work taken out: no company,
// no project, no rate. The onboarding itself is the same one.

export const createAdminSchema = z.object({
  name: z.string().trim().min(1).max(200),
  email: z.email().max(254),
  /** A full admin, or a user admin who manages contractors only (AUTH-13). */
  role: z.enum(STAFF_ROLES).default("ADMIN"),
});
export type CreateAdminInput = z.infer<typeof createAdminSchema>;

export const updateAdminSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    email: z.email().max(254).optional(),
    role: z.enum(STAFF_ROLES).optional(),
  })
  .refine(notEmpty, atLeastOneField);
export type UpdateAdminInput = z.infer<typeof updateAdminSchema>;

/**
 * Narrower than `updateContractorStatusSchema` on purpose: that one still accepts
 * INVITED, and nothing should ever post an admin *back* into the pending state —
 * an activated account cannot be un-activated, only disabled.
 */
export const setAdminStatusSchema = z.object({
  status: z.enum(["ACTIVE", "INACTIVE"]),
});
export type SetAdminStatusInput = z.infer<typeof setAdminStatusSchema>;

export const updateClientSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    // `.optional()` goes last: a `.transform()` after it produces a *required* key
    // typed `string | undefined`, which then forces every caller to pass the field.
    shortCode: z
      .string()
      .trim()
      .min(1)
      .max(4)
      .regex(/^[A-Za-z0-9]+$/, "Letters and digits only")
      .transform((v) => v.toUpperCase())
      .optional(),
    /** `null` clears the contact; omitting the key leaves it alone. */
    primaryContactName: clearableText(200),
    primaryContactEmail: clearableEmail,
    /** INACTIVE is the archive: it disappears from pickers and keeps its history. */
    status: z.enum(USER_STATUSES).optional(),
  })
  .refine(notEmpty, atLeastOneField);
export type UpdateClientInput = z.infer<typeof updateClientSchema>;

export const updateProjectSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    code: z
      .string()
      .trim()
      .min(2)
      .max(40)
      .regex(/^[A-Za-z0-9-]+$/, "Letters, digits and hyphens only")
      .transform((v) => v.toUpperCase())
      .optional(),
    /** CLOSED is the archive. Nothing new can be assigned to it. */
    status: z.enum(PROJECT_STATUSES).optional(),
    startDate: ymdSchema.nullable().optional(),
    endDate: ymdSchema.nullable().optional(),
    budget: z
      .number()
      .nonnegative()
      .max(999_999_999_999.99)
      .multipleOf(0.01)
      .nullable()
      .optional(),
    /**
     * Optional default **day** rate (S3.2), captured for future
     * reconciliation/analytics. `null` clears it. It never prices a timesheet —
     * it only prefills a new assignment's first rate window.
     */
    defaultRate: z.number().nonnegative().nullable().optional(),
  })
  .refine(notEmpty, atLeastOneField)
  .refine((v) => !v.startDate || !v.endDate || v.endDate >= v.startDate, {
    message: "End date must be on or after the start date.",
    path: ["endDate"],
  });
export type UpdateProjectInput = z.infer<typeof updateProjectSchema>;

export const createClientSchema = z.object({
  name: z.string().trim().min(1).max(200),
  /** Stored as VARCHAR(4); derived from the name when omitted. */
  shortCode: z
    .string()
    .trim()
    .min(1)
    .max(4)
    .regex(/^[A-Za-z0-9]+$/, "Letters and digits only")
    .optional()
    .transform((v) => v?.toUpperCase()),
  primaryContactName: optionalText(200),
  primaryContactEmail: optionalEmail,
});
export type CreateClientInput = z.infer<typeof createClientSchema>;

export const createProjectSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    code: z
      .string()
      .trim()
      .min(2)
      .max(40)
      .regex(/^[A-Za-z0-9-]+$/, "Letters, digits and hyphens only")
      .transform((v) => v.toUpperCase()),
    clientId: z.string().min(1).max(64),
    status: z.enum(PROJECT_STATUSES).optional().default("ACTIVE"),
    startDate: ymdSchema.optional(),
    endDate: ymdSchema.optional(),
    budget: z
      .number()
      .nonnegative()
      .max(999_999_999_999.99)
      .multipleOf(0.01)
      .nullable()
      .optional(),
    /** Optionally create the first assignments with the project. */
    contractorIds: z
      .array(z.string().min(1).max(64))
      .max(100)
      .refine((ids) => new Set(ids).size === ids.length, {
        message: "Select each contractor only once.",
      })
      .optional(),
    /** Optional default **day** rate (S3.2); prefills a new assignment's rate. */
    defaultRate: z.number().nonnegative().nullable().optional(),
  })
  // YYYY-MM-DD sorts lexicographically, so a string compare is a date compare.
  .refine((v) => !v.startDate || !v.endDate || v.endDate >= v.startDate, {
    message: "End date must be on or after the start date.",
    path: ["endDate"],
  });
export type CreateProjectInput = z.infer<typeof createProjectSchema>;

// --- assignments -----------------------------------------------------------
// An assignment is the engagement of one contractor on one project. It exists
// on its own, rather than only as a side effect of creating a contractor, so
// that somebody who moves from one client to the next can actually be moved.

const rateSchema = z.number().nonnegative().max(MAX_DAILY_RATE).multipleOf(RATE_STEP);

export const createAssignmentSchema = z
  .object({
    contractorId: z.string().min(1).max(64),
    projectId: z.string().min(1).max(64),
    /** Omitted means no rate window yet; uncovered work is reported as unpriced. */
    dailyRate: rateSchema.optional(),
    startDate: ymdSchema.optional(),
    endDate: ymdSchema.optional(),
  })
  .refine((v) => !v.startDate || !v.endDate || v.endDate >= v.startDate, {
    message: "End date must be on or after the start date.",
    path: ["endDate"],
  });
export type CreateAssignmentInput = z.infer<typeof createAssignmentSchema>;

/**
 * Every field optional: the same endpoint serves "set an end date" and "end this
 * engagement" (`active: false`). `null` clears a date — `undefined` leaves it
 * alone, which is why the dates are nullable rather than merely optional.
 *
 * **`dailyRate` is deliberately absent.** It used to live here, and that was the
 * bug: a rate set with no "from when" is a rate that has always been true, so
 * changing it rewrote what every past approved week had been worth. A rate change
 * is a different act from a correction to an engagement, and it goes through
 * `POST /api/manage/assignments/{id}/rates`, which makes you say from when.
 */
export const updateAssignmentSchema = z
  .object({
    startDate: ymdSchema.nullable().optional(),
    endDate: ymdSchema.nullable().optional(),
    active: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: "Nothing to update.",
  })
  .refine((v) => !v.startDate || !v.endDate || v.endDate >= v.startDate, {
    message: "End date must be on or after the start date.",
    path: ["endDate"],
  });
export type UpdateAssignmentInput = z.infer<typeof updateAssignmentSchema>;

export const assignmentsQuerySchema = z.object({
  contractorId: z.string().min(1).max(64).optional(),
  projectId: z.string().min(1).max(64).optional(),
});
export type AssignmentsQuery = z.infer<typeof assignmentsQuerySchema>;

// --- rates -----------------------------------------------------------------

/**
 * Set the rate on an assignment, **from a date** (RATE-1, RATE-4).
 *
 * `amount` is in `unit`s — 800 a day, or 100 an hour — because that is how a rate
 * is agreed and how an admin thinks about it. The service converts to the hourly
 * rate it stores (RATE-3); nothing downstream has to know which unit was typed.
 *
 * `payAmount` is nullable and defaults to absent, because it usually is: nobody
 * has recorded a cost for the engagements that exist today. Absent stays absent —
 * a missing pay rate must never be filled in as zero (RATE-9).
 */
export const setAssignmentRateSchema = z
  .object({
    amount: z.number().nonnegative().multipleOf(RATE_STEP),
    payAmount: z.number().nonnegative().multipleOf(RATE_STEP).nullable().optional(),
    unit: z.enum(RATE_UNITS).optional().default("DAY"),
    currency: z.enum(CURRENCIES).optional().default("AUD"),
    effectiveFrom: ymdSchema,
  })
  // The ceiling depends on the unit: a rate is bounded by what it means per day,
  // whether it was typed per day or per hour.
  .superRefine((v, ctx) => {
    const max = v.unit === "DAY" ? MAX_DAILY_RATE : MAX_HOURLY_RATE;
    for (const field of ["amount", "payAmount"] as const) {
      const value = v[field];
      if (value != null && value > max) {
        ctx.addIssue({
          code: "custom",
          path: [field],
          message: `A ${v.unit === "DAY" ? "daily" : "hourly"} rate can't exceed ${max}.`,
        });
      }
    }
  });
export type SetAssignmentRateInput = z.infer<typeof setAssignmentRateSchema>;

// --- Contractor invoices (CINV) ----------------------------------------------

/** The multipart text field beside the PDF on upload: which month this invoice is for. */
export const uploadContractorInvoiceSchema = z.object({ period: yearMonthSchema });
export type UploadContractorInvoiceInput = z.infer<typeof uploadContractorInvoiceSchema>;

/**
 * An admin uploading a contractor's invoice for them: the contractor's own
 * upload, with whose invoice it is named. Same month rule, same file rules —
 * the service runs the contractor's path, so CINV-1/10/13 hold unchanged.
 */
export const uploadInvoiceOnBehalfSchema = uploadContractorInvoiceSchema.extend({
  contractorId: z.string().min(1).max(64),
});
export type UploadInvoiceOnBehalfInput = z.infer<typeof uploadInvoiceOnBehalfSchema>;

// --- Invoice intake (CINV-21) ---------------------------------------------------

/** Ids as one comma-separated query value: what a batch polls on. */
const idList = z
  .string()
  .transform((v) => [...new Set(v.split(",").map((id) => id.trim()).filter(Boolean))])
  .pipe(z.array(z.string().min(1).max(64)).min(1).max(MAX_INTAKE_PAGE));

export const invoiceIntakesQuerySchema = z.object({
  /** These intakes, whatever their status — a batch being reviewed. Otherwise every pending one. */
  ids: idList.optional(),
  /** Only `pendingCount` — the inbox's nudge, which needs no suggestions. */
  countOnly: z
    .enum(["true", "false"])
    .optional()
    .transform((v) => v === "true"),
});
export type InvoiceIntakesQuery = z.infer<typeof invoiceIntakesQuerySchema>;
export type InvoiceIntakesQueryInput = z.input<typeof invoiceIntakesQuerySchema>;

/** Filing an intake: whose invoice it is and for which month — what an upload would have said. */
export const fileInvoiceIntakeSchema = uploadInvoiceOnBehalfSchema.extend({ version: z.number().int().nonnegative() });
export type FileInvoiceIntakeInput = z.infer<typeof fileInvoiceIntakeSchema>;

export const discardInvoiceIntakeSchema = z.object({ version: z.number().int().nonnegative() });

/** A contractor filing their own intake: the month alone — whose it is is already known. */
export const fileMyInvoiceIntakeSchema = uploadContractorInvoiceSchema.extend({ version: z.number().int().nonnegative() });
export type FileMyInvoiceIntakeInput = z.infer<typeof fileMyInvoiceIntakeSchema>;
export type DiscardInvoiceIntakeInput = z.infer<typeof discardInvoiceIntakeSchema>;

/** One contractor's invoiced months, for a month picker that should not offer a taken one. */
export const invoiceMonthsQuerySchema = z.object({ contractorId: z.string().min(1).max(64) });
export type InvoiceMonthsQuery = z.infer<typeof invoiceMonthsQuerySchema>;

export const myInvoicesQuerySchema = z.object({
  status: z
    .enum([...CONTRACTOR_INVOICE_STATUSES, "ALL"])
    .optional()
    .default("ALL"),
  page: pageSchema,
  pageSize: pageSizeSchema,
});
export type MyInvoicesQuery = z.infer<typeof myInvoicesQuerySchema>;
export type MyInvoicesQueryInput = z.input<typeof myInvoicesQuerySchema>;

/** `YYYY-MM` sorts lexicographically, so a string compare is a month compare. */
const monthRangeOk = (v: { from?: string; to?: string }) =>
  !v.from || !v.to || v.to >= v.from;

export const adminInvoicesQuerySchema = z
  .object({
    /** Free-text over contractor name/email/company, invoice number and supplier. */
    q: z.string().trim().max(120).optional(),
    contractorId: z.string().min(1).max(64).optional(),
    status: z
      .enum([...CONTRACTOR_INVOICE_STATUSES, "ALL"])
      .optional()
      .default("SUBMITTED"),
    validationStatus: z.enum(INVOICE_VALIDATION_STATUSES).optional(),
    /** A project's id, or UNASSIGNED_PROJECT for invoices filed under none (CINV-20). */
    projectId: z.string().min(1).max(64).optional(),
    /** Invoices with at least one line still to file — partly tagged ones included. */
    untaggedOnly: z
      .enum(["true", "false"])
      .optional()
      .transform((v) => v === "true"),
    /** Approved or paid invoices whose approved hours have fallen since (CINV-28). */
    changedOnly: z
      .enum(["true", "false"])
      .optional()
      .transform((v) => v === "true"),
    from: yearMonthSchema.optional(),
    to: yearMonthSchema.optional(),
    sort: z.enum(INVOICE_SORT_FIELDS).optional().default("submittedAt"),
    dir: z.enum(SORT_DIRECTIONS).optional().default("desc"),
    page: pageSchema,
    pageSize: pageSizeSchema,
  })
  .refine(monthRangeOk, {
    message: "The end month must be on or after the start month.",
    path: ["to"],
  });
export type AdminInvoicesQuery = z.infer<typeof adminInvoicesQuerySchema>;
export type AdminInvoicesQueryInput = z.input<typeof adminInvoicesQuerySchema>;

/**
 * The payment history: PAID invoices, by the day they were paid. Dates, not
 * months — a payment run is a day.
 */
export const adminPaymentsQuerySchema = z
  .object({
    /** Free-text over contractor name/company, invoice number and payment reference. */
    q: z.string().trim().max(120).optional(),
    contractorId: z.string().min(1).max(64).optional(),
    /** Contractor invoices, expenses, or both (the default). */
    kind: z.enum(["CONTRACTOR", "EXPENSE"]).optional(),
    from: ymdSchema.optional(),
    to: ymdSchema.optional(),
    sort: z.enum(PAYMENT_SORT_FIELDS).optional().default("paidAt"),
    dir: z.enum(SORT_DIRECTIONS).optional().default("desc"),
    page: pageSchema,
    pageSize: pageSizeSchema,
  })
  .refine(monthRangeOk, {
    message: "The end date must be on or after the start date.",
    path: ["to"],
  });
export type AdminPaymentsQuery = z.infer<typeof adminPaymentsQuerySchema>;
export type AdminPaymentsQueryInput = z.input<typeof adminPaymentsQuerySchema>;

/** The version the admin read (compare-and-swap, as timesheets do with TIME-11). */
const invoiceVersionSchema = z.number().int().nonnegative();

/**
 * Tag invoice lines to projects (CINV-20). An overlay like the field
 * corrections: a tag with a project overrides the match, a tag with null says
 * "this line is on no project", and `revert` drops a tag so the match shows
 * again (it also clears a tag whose line is gone).
 */
export const tagInvoiceLinesSchema = z
  .object({
    version: invoiceVersionSchema,
    tags: z
      .array(
        z.object({
          lineKey: z.string().min(1).max(300),
          projectId: z.string().min(1).max(64).nullable(),
        })
      )
      .max(MAX_INVOICE_LINES)
      .optional()
      .default([]),
    revert: z.array(z.string().min(1).max(300)).max(MAX_INVOICE_LINES).optional().default([]),
  })
  .refine((v) => v.tags.length > 0 || v.revert.length > 0, { message: "Nothing to update." });
export type TagInvoiceLinesInput = z.infer<typeof tagInvoiceLinesSchema>;

/** File the whole invoice under a project, or (null) take it off (CINV-35). Carries the version read. */
export const setInvoiceProjectSchema = z.object({
  version: invoiceVersionSchema,
  projectId: z.string().min(1).max(64).nullable(),
});
export type SetInvoiceProjectInput = z.infer<typeof setInvoiceProjectSchema>;

/**
 * File several invoices under one project from the inbox, or (null) take it off
 * them all (CINV-35). No versions, as for expenses: each row is still its own
 * compare-and-swap, and one changed meanwhile is reported, never undone.
 */
export const assignInvoiceProjectSchema = z.object({
  ids: z
    .array(z.string().min(1).max(64))
    .min(1)
    .max(MAX_PAGE_SIZE)
    .refine((ids) => new Set(ids).size === ids.length, { message: "Each invoice once." }),
  projectId: z.string().min(1).max(64).nullable(),
});
export type AssignInvoiceProjectInput = z.infer<typeof assignInvoiceProjectSchema>;

const moneyField = z
  .number()
  .nonnegative()
  .max(999_999_999.99)
  .multipleOf(0.01)
  .nullable()
  .optional();
const hoursField = z
  .number()
  .nonnegative()
  .max(9_999.99)
  .multipleOf(0.01)
  .nullable()
  .optional();

/**
 * An admin's corrections to what the model read (CINV-7).
 *
 * `fields` is an overlay: a key with a value overrides the read value, a key with
 * `null` says "this is not on the invoice", an absent key leaves the read value
 * alone. `revert` drops an earlier override so the AI's value shows again.
 */
/**
 * The correctable fields with their bounds — one definition, used by every
 * overlay: an admin's corrections to a contractor invoice, and the fields of an
 * expense typed by hand (EXP).
 */
const invoiceFieldOverlaySchema = z.object({
      invoiceNumber: clearableText(100),
      invoiceDate: ymdSchema.nullable().optional(),
      description: clearableText(500),
      supplierName: clearableText(200),
      /** Digits only after the service strips spaces; bounded so a pasted sentence is refused. */
      supplierAbn: clearableText(20),
      supplierGstin: clearableText(20),
      currency: z.enum(CURRENCIES).nullable().optional(),
      periodStart: ymdSchema.nullable().optional(),
      periodEnd: ymdSchema.nullable().optional(),
      hoursClaimed: hoursField,
      daysClaimed: hoursField,
      unitRate: moneyField,
      rateUnit: z.enum(RATE_UNITS).nullable().optional(),
      labourAmount: moneyField,
      otherCharges: moneyField,
      subtotal: moneyField,
      taxRatePct: z.number().min(0).max(100).multipleOf(0.01).nullable().optional(),
      taxAmount: moneyField,
      total: moneyField,
});

export const correctInvoiceFieldsSchema = z
  .object({
    version: invoiceVersionSchema,
    fields: invoiceFieldOverlaySchema.optional().default({}),
    revert: z
      .array(z.enum(CORRECTABLE_FIELDS))
      .max(CORRECTABLE_FIELDS.length)
      .optional()
      .default([]),
  })
  .refine(
    (v) => Object.values(v.fields).some((x) => x !== undefined) || v.revert.length > 0,
    {
      message: "Nothing to update.",
    }
  )
  .refine(
    (v) =>
      !v.fields.periodStart ||
      !v.fields.periodEnd ||
      v.fields.periodEnd >= v.fields.periodStart,
    { message: "The period must end on or after it starts.", path: ["periodEnd"] }
  );
export type CorrectInvoiceFieldsInput = z.infer<typeof correctInvoiceFieldsSchema>;

export const reviewInvoiceSchema = z.object({ version: invoiceVersionSchema });
export type ReviewInvoiceInput = z.infer<typeof reviewInvoiceSchema>;

/**
 * Approve for payment. Over a failing check the reason is required (CINV-34) and
 * is saved as the invoice's explanation (CINV-27); otherwise it may be left out.
 */
export const approveInvoiceSchema = z.object({
  version: invoiceVersionSchema,
  reason: z.string().trim().min(3).max(MAX_COMMENT_LENGTH).optional(),
});
export type ApproveInvoiceInput = z.infer<typeof approveInvoiceSchema>;

/** Request correction / reject: the note is what the contractor is emailed. */
export const returnInvoiceSchema = z.object({
  version: invoiceVersionSchema,
  note: z.string().trim().min(3).max(MAX_COMMENT_LENGTH),
});
export type ReturnInvoiceInput = z.infer<typeof returnInvoiceSchema>;

/** Why the flags on an invoice are fine (CINV-27). Admin-only; the contractor never sees it. */
export const explainInvoiceSchema = z.object({
  version: invoiceVersionSchema,
  note: z.string().trim().min(3).max(MAX_COMMENT_LENGTH),
});
export type ExplainInvoiceInput = z.infer<typeof explainInvoiceSchema>;

/** A DELETE's version rides on the query string. */
export const invoiceVersionQuerySchema = z.object({
  version: z.coerce.number().int().nonnegative(),
});

/** A payment against an invoice (CINV-23): above zero, in cents. The balance is the service's to check. */
const paymentAmountSchema = z.number().positive().max(999_999_999.99).multipleOf(0.01);

/**
 * Record a payment — the whole balance or part of it (CINV-23). The amount is
 * in the invoice's own currency; there is no other to pay it in (CINV-16).
 */
export const recordInvoicePaymentSchema = z.object({
  version: invoiceVersionSchema,
  amount: paymentAmountSchema,
  /** Defaults to today when omitted; a back-dated payment names its own day. */
  paidOn: ymdSchema.optional(),
  reference: optionalText(MAX_PAYMENT_REFERENCE_LENGTH),
});
export type RecordInvoicePaymentInput = z.infer<typeof recordInvoicePaymentSchema>;

/** Correct a recorded payment. Only what is sent changes; a blank or null reference clears it. */
export const updateInvoicePaymentSchema = z
  .object({
    version: invoiceVersionSchema,
    amount: paymentAmountSchema.optional(),
    paidOn: ymdSchema.optional(),
    reference: z
      .string()
      .trim()
      .max(MAX_PAYMENT_REFERENCE_LENGTH)
      .nullable()
      .transform((v) => (v ? v : null))
      .optional(),
  })
  .refine((v) => v.amount !== undefined || v.paidOn !== undefined || v.reference !== undefined, {
    message: "Nothing to update.",
  });
export type UpdateInvoicePaymentInput = z.infer<typeof updateInvoicePaymentSchema>;

/** A DELETE carries no body, so the version it read rides on the query string. */
export const removeInvoicePaymentQuerySchema = z.object({
  version: z.coerce.number().int().nonnegative(),
});
export type RemoveInvoicePaymentQuery = z.infer<typeof removeInvoicePaymentQuerySchema>;

/** Close as paid with a balance left (CINV-23). The reason is what the contractor is told. */
export const closeInvoiceShortSchema = z.object({
  version: invoiceVersionSchema,
  reason: z.string().trim().min(3).max(MAX_COMMENT_LENGTH),
});
export type CloseInvoiceShortInput = z.infer<typeof closeInvoiceShortSchema>;

// --- Expense invoices (EXP) ---------------------------------------------------

/** An empty multipart text field means "not given". */
const blankAsUndefined = (v: unknown) => (v === "" || v === null ? undefined : v);

export const adminExpensesQuerySchema = z
  .object({
    /** Free-text over vendor, invoice number and description. */
    q: z.string().trim().max(120).optional(),
    /** A project's id, or UNASSIGNED_PROJECT for expenses on none. */
    projectId: z.string().min(1).max(64).optional(),
    /** A vendor: its `supplierKey`. */
    vendor: z.string().min(1).max(200).optional(),
    category: z.enum(EXPENSE_CATEGORIES).optional(),
    status: z.enum([...EXPENSE_INVOICE_STATUSES, "ALL"]).optional().default("ALL"),
    validationStatus: z.enum(EXPENSE_VALIDATION_STATUSES).optional(),
    /** Only uploads whose details nobody has confirmed yet (EXP-8). */
    needsDetails: z
      .enum(["true", "false"])
      .optional()
      .transform((v) => v === "true"),
    /** Dates, not months: an expense is a day. */
    from: ymdSchema.optional(),
    to: ymdSchema.optional(),
    sort: z.enum(EXPENSE_SORT_FIELDS).optional().default("expenseDate"),
    dir: z.enum(SORT_DIRECTIONS).optional().default("desc"),
    page: pageSchema,
    pageSize: pageSizeSchema,
  })
  .refine(monthRangeOk, { message: "The end date must be on or after the start date.", path: ["to"] });
export type AdminExpensesQuery = z.infer<typeof adminExpensesQuerySchema>;
export type AdminExpensesQueryInput = z.input<typeof adminExpensesQuerySchema>;

const expenseAttributes = {
  projectId: z.string().min(1).max(64).nullable().optional(),
  category: z.enum(EXPENSE_CATEGORIES).nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
};

/** Record an expense by hand: no document, every field typed. */
export const createExpenseInvoiceSchema = z
  .object({
    ...expenseAttributes,
    expenseDate: ymdSchema,
    fields: invoiceFieldOverlaySchema,
  })
  .refine((v) => Boolean(v.fields.supplierName) || v.fields.total != null, {
    message: "Enter at least the supplier or the total.",
    path: ["fields"],
  });
export type CreateExpenseInvoiceInput = z.infer<typeof createExpenseInvoiceSchema>;

/** The text fields beside an uploaded expense document. All optional: the document supplies the rest. */
export const uploadExpenseInvoiceSchema = z.object({
  projectId: z.preprocess(blankAsUndefined, z.string().min(1).max(64).optional()),
  category: z.preprocess(blankAsUndefined, z.enum(EXPENSE_CATEGORIES).optional()),
  expenseDate: z.preprocess(blankAsUndefined, ymdSchema.optional()),
});
export type UploadExpenseInvoiceInput = z.infer<typeof uploadExpenseInvoiceSchema>;

/** The attributes a suggestion can prefill (EXP-8). */
export const SUGGESTIBLE_EXPENSE_FIELDS = ["projectId", "category", "expenseDate"] as const;
export type SuggestibleExpenseField = (typeof SUGGESTIBLE_EXPENSE_FIELDS)[number];

/** An expense's own attributes — not the document's fields, which go through `fields`. */
export const updateExpenseInvoiceSchema = z
  .object({
    version: invoiceVersionSchema,
    ...expenseAttributes,
    expenseDate: ymdSchema.optional(),
    /**
     * Which of the values sent were a suggestion the admin accepted unchanged.
     * Recorded on the event and nowhere else — it is how often the suggestions
     * are right, not a fact about the expense.
     */
    fromSuggestion: z.array(z.enum(SUGGESTIBLE_EXPENSE_FIELDS)).max(SUGGESTIBLE_EXPENSE_FIELDS.length).optional(),
  })
  .refine(
    (v) =>
      v.projectId !== undefined ||
      v.category !== undefined ||
      v.note !== undefined ||
      v.expenseDate !== undefined,
    { message: "Nothing to update." }
  );
export type UpdateExpenseInvoiceInput = z.infer<typeof updateExpenseInvoiceSchema>;

/**
 * File several expenses under one project at once, from the list (EXP-8). No
 * versions: the admin chose rows, not their contents, and the one attribute
 * set is the same whatever else changed — each row is still a compare-and-swap.
 */
export const assignExpenseProjectSchema = z.object({
  ids: z
    .array(z.string().min(1).max(64))
    .min(1)
    .max(MAX_PAGE_SIZE)
    .refine((ids) => new Set(ids).size === ids.length, { message: "Each expense once." }),
  projectId: z.string().min(1).max(64).nullable(),
});
export type AssignExpenseProjectInput = z.infer<typeof assignExpenseProjectSchema>;

/** Delete a void expense for good (EXP-12): the version the admin saw, in the query. */
export const deleteExpenseQuerySchema = z.object({
  version: z.coerce.number().int().nonnegative(),
});
export type DeleteExpenseQuery = z.infer<typeof deleteExpenseQuerySchema>;

// --- GST by BAS quarter ---------------------------------------------------------

/** The most quarters one GST report spans: three financial years. Bounded (DATA-7). */
export const MAX_GST_QUARTERS = 12;

const basQuarterKey = z.string().refine((v) => parseBasQuarter(v) !== null, { message: "Not a BAS quarter." });

export const gstReportQuerySchema = z
  .object({
    /** "FY2027-Q1". Absent: the four quarters to the current one. */
    from: basQuarterKey.optional(),
    to: basQuarterKey.optional(),
    /** APPROVED is approved and paid; APPROVED_AND_SUBMITTED adds those awaiting a decision. */
    scope: z.enum(REPORT_SCOPES).optional().default("APPROVED"),
  })
  .refine((v) => !v.from || !v.to || v.to >= v.from, { message: "The last quarter must not be before the first.", path: ["to"] })
  .refine(
    (v) =>
      !v.from ||
      !v.to ||
      basQuartersBetween(parseBasQuarter(v.from)!, parseBasQuarter(v.to)!).length <= MAX_GST_QUARTERS,
    { message: `At most ${MAX_GST_QUARTERS} quarters at a time.`, path: ["to"] }
  );
export type GstReportQuery = z.infer<typeof gstReportQuerySchema>;
export type GstReportQueryInput = z.input<typeof gstReportQuerySchema>;
